import { Capacitor } from '@capacitor/core';

// ── Types ──────────────────────────────────────────────────────────────────
export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'unavailable';

interface PermissionResult {
    location: PermissionStatus;
    storage: PermissionStatus;
    notifications: PermissionStatus;
    microphone: PermissionStatus;
}

// ── Helpers ────────────────────────────────────────────────────────────────
const isNative = () => Capacitor.isNativePlatform();

// ── Geolocation ────────────────────────────────────────────────────────────
export const requestLocationPermission = async (): Promise<PermissionStatus> => {
    if (isNative()) {
        try {
            const { Geolocation } = await import('@capacitor/geolocation');
            const status = await Geolocation.requestPermissions();
            // 'coarseLocation' | 'location'
            const granted = status.location === 'granted' || status.coarseLocation === 'granted';
            console.log('📍 Location permission:', status);
            return granted ? 'granted' : 'denied';
        } catch (e) {
            console.error('Location permission error:', e);
            return 'denied';
        }
    }

    // Web fallback
    if (!('geolocation' in navigator)) return 'unavailable';
    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            () => resolve('granted'),
            (err) => resolve(err.code === 1 ? 'denied' : 'prompt')
        );
    });
};

// ── Storage / Media (Gallery Access Only - No Camera) ─────────────────────
export const requestStoragePermission = async (): Promise<PermissionStatus> => {
    if (isNative()) {
        try {
            // Request photo library access only (no camera)
            const { Camera } = await import('@capacitor/camera');
            const status = await Camera.requestPermissions({ permissions: ['photos'] });
            const granted = status.photos === 'granted';
            console.log('� Storage/Media permission:', status);
            return granted ? 'granted' : 'denied';
        } catch (e) {
            console.error('Storage permission error:', e);
            return 'denied';
        }
    }

    // Web: file input doesn't need explicit permission
    return 'granted';
};

// ── Notifications ──────────────────────────────────────────────────────────
export const requestNotificationPermission = async (): Promise<PermissionStatus> => {
    if (isNative()) {
        try {
            const { LocalNotifications } = await import('@capacitor/local-notifications');
            const { PushNotifications } = await import('@capacitor/push-notifications');

            const localStatus = await LocalNotifications.requestPermissions();
            const pushStatus = await PushNotifications.requestPermissions();

            const granted = localStatus.display === 'granted' || pushStatus.receive === 'granted';
            console.log('🔔 Notification permission:', { localStatus, pushStatus });
            return granted ? 'granted' : 'denied';
        } catch (e) {
            console.error('Notification permission error:', e);
            return 'denied';
        }
    }

    // Web fallback
    if (!('Notification' in window)) return 'unavailable';
    const result = await Notification.requestPermission();
    return result === 'granted' ? 'granted' : result === 'denied' ? 'denied' : 'prompt';
};

// ── Microphone ─────────────────────────────────────────────────────────────
export const requestMicrophonePermission = async (): Promise<PermissionStatus> => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(t => t.stop());
        return 'granted';
    } catch {
        return 'denied';
    }
};

// ── Phone Calls (opens dialer on Android, no runtime permission needed for VIEW intent) ──
export const makePhoneCall = (phoneNumber: string): void => {
    window.open(`tel:${phoneNumber}`, '_self');
};

// ── Check current permission status ───────────────────────────────────────
export const checkAllPermissions = async (): Promise<PermissionResult> => {
    const result: PermissionResult = {
        location: 'prompt',
        storage: 'prompt',
        notifications: 'prompt',
        microphone: 'prompt',
    };

    if (isNative()) {
        try {
            const { Geolocation } = await import('@capacitor/geolocation');
            const geo = await Geolocation.checkPermissions();
            result.location = (geo.location === 'granted' || geo.coarseLocation === 'granted') ? 'granted' : geo.location as PermissionStatus;
        } catch { }

        try {
            const { Camera } = await import('@capacitor/camera');
            const cam = await Camera.checkPermissions();
            result.storage = cam.photos as PermissionStatus;
        } catch { }

        try {
            const { LocalNotifications } = await import('@capacitor/local-notifications');
            const notif = await LocalNotifications.checkPermissions();
            result.notifications = notif.display as PermissionStatus;
        } catch { }
    } else {
        // Web: storage/file access doesn't need explicit permission
        result.storage = 'granted';
        if (navigator.permissions) {
            try {
                const geo = await navigator.permissions.query({ name: 'geolocation' });
                result.location = geo.state === 'granted' ? 'granted' : geo.state === 'denied' ? 'denied' : 'prompt';
            } catch { }
        }
        result.notifications = ('Notification' in window)
            ? (Notification.permission === 'granted' ? 'granted' : Notification.permission === 'denied' ? 'denied' : 'prompt')
            : 'unavailable';
    }

    return result;
};

// ── Master: request ALL required permissions at app start ─────────────────
const PERMS_REQUESTED_KEY = 'hh_perms_requested_v1';

export const requestAllPermissionsOnFirstLaunch = async (): Promise<void> => {
    const alreadyRequested = localStorage.getItem(PERMS_REQUESTED_KEY);
    if (alreadyRequested === 'true') return;

    console.log('🔐 Requesting all required app permissions...');

    // Sequential requests give user clear context for each permission
    await requestNotificationPermission();
    await requestLocationPermission();
    await requestStoragePermission(); // Storage/media only (no camera)
    await requestMicrophonePermission();

    localStorage.setItem(PERMS_REQUESTED_KEY, 'true');
    console.log('✅ All permissions requested');
};
